import { cloneDeep } from 'lodash';
import { EdgelessObject, normalizeResultScale, type IFramework, type IResultOptions } from '@/framework';
import { parseSVG, serializeSVGWithOpacity } from '@/utils/svg-kit';
import { MAX_STAGE_WIDTH } from '@/version-types';
import { BrushInteractionPreview, canPreviewBrushInteraction } from './render/interaction-preview';
import { renderLayerContentToCanvas } from './render/render-to-canvas';
import { BrushCreator } from './utils/brush-creator';

export class EdgelessBrushLayer extends EdgelessObject {
    private identity: string;
    private interactionPreview = new BrushInteractionPreview();
    private previewActive = false;
    private resourceScale = 0;
    private resourceScales = new WeakMap<HTMLCanvasElement, number>();

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
        this.identity = framework.identity;
    }

    private getDisplayResourceScale() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const { zoom = 1, retinaScaling } = this.service.edgelessCanvas;
        const frame =
            typeof layer.__stickyFrameId === 'number'
                ? this.service.frame.get(layer.__stickyFrameId)
                : this.service.frame.get(layer);
        // Frame 缓存/缩略图不随视口倍率重绘；特殊变换和 AI 水印也保留原资源精度。
        const viewportScale = !frame && canPreviewBrushInteraction(layer) ? zoom * retinaScaling : Infinity;
        return Math.min(
            super.getResourceScale(),
            viewportScale,
            MAX_STAGE_WIDTH / Math.max(1, layer.width),
            MAX_STAGE_WIDTH / Math.max(1, layer.height),
        );
    }

    private async getResource(scale: number, layer = cloneDeep(this.service.flatLayer.getLayer(this.layerId))) {
        return renderLayerContentToCanvas(layer, this.identity, false, scale);
    }

    override supportsCanvasResult() {
        return true;
    }

    override supportsSvgResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = cloneDeep(this.service.flatLayer.getLayer(this.layerId));
        const renderBox = this.getBaseInfo();
        const svgRaw = await BrushCreator.getBrushSvgRawAsync(layer, layer.id);
        const svgRoot = layer.opacity < 1 ? parseSVG(svgRaw) : null;
        const svg = svgRoot ? serializeSVGWithOpacity(svgRoot, layer.opacity).svgText : svgRaw;
        const canvas = await this.getResource(normalizeResultScale(scale), layer);
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { svg, canvas, renderBox };
    }

    override async loadResource() {
        const scale = this.getDisplayResourceScale();
        const canvas = await this.getResource(scale);
        this.resourceScales.set(canvas, scale);
        await super.drawAiWatermarkAsync(canvas);
        return canvas;
    }

    /**
     * 交互中同步更新几何信息，结束后沿用正式资源生成流程
     * @param onlyUpdateBaseInfo - 当前操作是否仅更新基础信息
     * @param isAdd - 是否为首次添加对象，首次添加仍需生成正式资源
     * @returns 更新调度完成的 Promise，不代表异步资源已经生成完毕
     */
    override async update(onlyUpdateBaseInfo: boolean, isAdd = false) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const canPreview = canPreviewBrushInteraction(layer);
        // 新增图层立刻具备可操作的实时画面，不等待首份异步 resource。
        if (isAdd && canPreview) this.previewActive = true;
        const isChangingSize = onlyUpdateBaseInfo && (this.width !== layer.width || this.height !== layer.height);
        if (!isAdd && canPreview && (layer.__isBasicOperating || isChangingSize)) {
            this.previewActive = true;
            this.invalidatePendingResource();
            return super.update(true);
        }
        if (this.previewActive) {
            if (!canPreview) {
                this.previewActive = false;
                this.interactionPreview.clear();
            } else {
                // 松手后的正式资源异步生成期间继续显示最新实时笔迹，避免回跳到旧 resource。
                void super.update(true);
            }
        }
        return super.update(onlyUpdateBaseInfo, isAdd);
    }

    /**
     * 正式资源通过版本检查后结束实时预览并释放临时像素缓冲
     * @param resource - 当前版本已接纳的资源
     */
    protected override onResourceSettled(resource: unknown) {
        if (resource instanceof HTMLCanvasElement) this.resourceScale = this.resourceScales.get(resource) || 0;
        this.previewActive = false;
        this.interactionPreview.clear();
    }

    /**
     * 在原图层位置合成实时笔迹，保持图层顺序、画布裁剪和整体透明度
     * @param ctx - 已应用场景到目标坐标变换的舞台上下文，函数会绘制当前图层
     */
    override render(ctx: CanvasRenderingContext2D) {
        // 显示资源按实际倍率生成；放大视图时先用精确预览接管，再生成更清晰的缓存。
        if (
            !this.previewActive &&
            this.resourceScale > 0 &&
            this.getDisplayResourceScale() > this.resourceScale * 1.05
        ) {
            const layer = this.service.flatLayer.getLayer(this.layerId);
            if (canPreviewBrushInteraction(layer)) {
                this.previewActive = true;
                void super.update(false);
            }
        }
        if (!this.previewActive) return super.render(ctx);
        if (this.noRender || this.domRender || this.width <= 0 || this.height <= 0) return;
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const { size, retinaScaling } = this.service.edgelessCanvas;
        try {
            const painted = this.interactionPreview.render(ctx, layer, {
                width: size.width * retinaScaling,
                height: size.height * retinaScaling,
            });
            if (painted) {
                this.service.svipWatermark.drawLayerWatermark(this, ctx);
                return;
            }
        } catch {
            this.interactionPreview.clear();
        }
        super.render(ctx);
    }
}
